import { useStore } from '@/store/useStore';
import { Filter, FileSpreadsheet, Plus, Trash2, Pencil, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTzShiftMs } from '@/hooks/useTzShift';
import type { Deal } from '@/types';
import {
  CURRENCY_LABEL, DEAL_STATUS_LABEL, DEAL_TO_FUNNEL, EVENT_PLACE_LABEL,
  FUNNEL_BY_KEY, SCREEN_TYPE_LABEL, dealEmoji,
} from '@/types';
import { okkConfig } from '@/data/okkForm';
import { cn } from '@/utils/cn';
import { downloadTableExcel } from '@/utils/exportTable';
import { DealEditor } from '@/components/modals/DealEditor';
import { GamesFilterModal, applyGameFilter, filterCount, EMPTY_FILTER, type GameFilter } from './GamesFilterModal';
import { usePerms } from '@/auth/usePerms';

// Название игры: своё имя, иначе «Студия 1.5 часа» из места+длительности.
function gameTitle(d: Deal): string {
  if (d.event.title && d.event.title.trim()) return d.event.title;
  const place = d.event.place === 'outside' ? 'Выезд' : 'Студия';
  const dur = d.event.duration_minutes
    ?? (d.event.start_at != null && d.event.end_at != null ? Math.round((d.event.end_at - d.event.start_at) / 60_000) : 60);
  const h = dur / 60;
  const word = h === 1 ? 'час' : (h >= 2 && h < 5) ? 'часа' : 'часов';
  return `${place} ${Number.isInteger(h) ? h : h} ${word}`;
}

const PAGE_SIZE = 100;

export function CrmPage() {
  const {
    deals, filterBranchId, toast, salesDealId, openSalesDeal,
    game_formats, sources, branches, occasions, employees, settings,
  } = useStore();
  const dperms = usePerms().deals;
  const tzShiftMs = useTzShiftMs();
  const [search, setSearch] = useState('');
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);
  const [gf, setGf] = useState<GameFilter>(EMPTY_FILTER);

  const fmtDateTime = (ts?: number) => ts != null
    ? new Date(ts + tzShiftMs).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
    : '—';
  // Для выгрузки дата и время нужны врозь: дата игры есть почти всегда, время — редко.
  // Пустую строку превращает в «—» сам exportTable, поэтому здесь возвращаем ''.
  const fmtDate = (ts?: number) => ts != null
    ? new Date(ts + tzShiftMs).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '';
  const fmtTime = (ts?: number) => ts != null
    ? new Date(ts + tzShiftMs).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    : '';

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let list = deals.filter((d) => filterBranchId === 'all' || d.branch_id === filterBranchId);
    list = applyGameFilter(list, gf);
    return list
      .filter((d) => {
        if (!q) return true;
        return d.number.toLowerCase().includes(q)
          || gameTitle(d).toLowerCase().includes(q)
          || d.contact_name.toLowerCase().includes(q)
          || (d.contact_phone || '').includes(q)
          || (d.event.address || '').toLowerCase().includes(q);
      })
      .sort((a, b) => (a.event.start_at ?? a.created_at) - (b.event.start_at ?? b.created_at));
  }, [deals, search, filterBranchId, gf]);

  // Соседи для листания карточки: весь отфильтрованный список в порядке таблицы
  // (не только текущая страница — листаем «сквозь» пагинацию).
  const navIds = useMemo(() => filtered.map((d) => d.id), [filtered]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const curPage = Math.min(page, pageCount - 1);
  const rows = filtered.slice(curPage * PAGE_SIZE, curPage * PAGE_SIZE + PAGE_SIZE);

  const allChecked = rows.length > 0 && rows.every((d) => checked.has(d.id));
  const toggleAll = () => {
    setChecked((prev) => {
      const s = new Set(prev);
      if (allChecked) rows.forEach((d) => s.delete(d.id));
      else rows.forEach((d) => s.add(d.id));
      return s;
    });
  };
  const toggleOne = (id: string) => setChecked((prev) => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const onDelete = (d: Deal) => {
    if (!confirm(`Удалить игру #${d.number}?`)) return;
    void useStore.getState().deleteDeal(d.id);
  };

  /**
   * Выгрузка отфильтрованного реестра.
   *
   * Состав столбцов повторяет выгрузку прежней системы заказчика (сверяли столбец в столбец),
   * поэтому здесь отдаём ВСЁ, что у нас реально есть, а не короткий набор из таблицы на экране.
   * Пустые значения приводит к «—» сам exportTable — так видно, что поле просто не заполнили
   * в CRM, а не что выгрузка его потеряла.
   *
   * Порядок: сначала «когда и что», потом клиент и деньги, потом параметры площадки,
   * в конце — служебные отметки и ответы анкеты ОКК (по столбцу на вопрос).
   */
  const exportExcel = () => {
    const nameById = (list: { id: string; name: string }[], id?: string | null) =>
      (id ? list.find((x) => x.id === id)?.name : '') ?? '';
    const yesNo = (v?: boolean) => (v === true ? 'Да' : v === false ? 'Нет' : '');
    // Дата игры — главное поле реестра: она приходит из Битрикса и заполнена почти везде,
    // в отличие от времени начала, которое проставляет управляющий уже здесь.
    const gameDate = (d: Deal) => d.event.event_date ?? d.event.start_at;

    const okk = okkConfig(settings.okk_questions);
    const okkQuestions = [...okk.required, ...okk.optional];

    const cols = [
      '№ игры', 'Название', 'Дата игры', 'Время начала', 'Время окончания', 'Длительность игры, мин',
      'Статус', 'Филиал',
      'Имя клиента', 'Телефон клиента', 'ПО ФИО', 'ПО Телефон',
      'Сумма', 'Откуда узнали', 'Фактическая сумма', 'Валюта', 'Предоплата', 'Чек предоплаты', 'Чек остатка',
      'Повод', 'Место проведения', 'Адрес', 'Формат игры',
      'Кол-во участников', 'Возраст участников', 'Язык', 'Играли ранее',
      'Дополнительные услуги', 'Сумма доп. услуг', 'Доплата за дальность',
      'Звук на площадке', 'Экран на площадке', 'Расчётный счёт',
      'Менеджер', 'Комментарий',
      'Дата создания', 'Дата изменения',
      ...okkQuestions.map((q) => `Отзыв: ${q.q}`),
    ];

    const rows = filtered.map((d) => {
      const e = d.event;
      const funnel = d.funnel_status ?? DEAL_TO_FUNNEL[d.status];
      const answers = d.okk?.answers ?? {};
      return [
        d.number, gameTitle(d),
        fmtDate(gameDate(d)), fmtTime(e.start_at), fmtTime(e.end_at),
        e.game_duration_minutes ?? e.duration_minutes ?? '',
        FUNNEL_BY_KEY.get(funnel)?.label ?? DEAL_STATUS_LABEL[d.status] ?? '',
        nameById(branches, d.branch_id),
        d.contact_name, d.contact_phone ?? '', e.responsible_name ?? '', e.responsible_phone ?? '',
        d.amount ?? '', nameById(sources, d.source_id), d.actual_amount ?? '', CURRENCY_LABEL[d.currency] ?? d.currency,
        d.payment.prepayment_amount ?? e.prepayment_note ?? '',
        d.payment.prepayment_receipts.length || '',
        d.payment.remainder_receipts.length || '',
        nameById(occasions, e.occasion_id),
        e.place ? EVENT_PLACE_LABEL[e.place] : '',
        e.address ?? '',
        nameById(game_formats, e.game_format_id),
        e.participant_count_text ?? e.participant_count ?? '',
        e.participant_age_text ?? '',
        e.language ?? '',
        yesNo(e.played_before),
        (e.services ?? []).join(', '),
        e.extra_services_amount ?? '',
        e.extra_distance_fee ?? '',
        yesNo(e.has_sound),
        e.screen_type ? SCREEN_TYPE_LABEL[e.screen_type] : '',
        yesNo(e.uses_invoice_account),
        (d.responsible_id ? employees.find((x) => x.id === d.responsible_id)?.full_name : '') || d.manager_name || '',
        e.comments ?? d.import_comment ?? '',
        fmtDateTime(d.created_at), fmtDateTime(d.updated_at),
        ...okkQuestions.map((q) => answers[q.id] ?? ''),
      ];
    });

    downloadTableExcel(`Игры_${new Date().toISOString().slice(0, 10)}.xlsx`, cols, rows);
    toast({ kind: 'success', text: `Выгружено игр: ${filtered.length}` });
  };

  // Полноэкранная карточка игры (Продажи → Игры).
  if (salesDealId !== null) {
    return <DealEditor chrome="page" id={salesDealId === 'new' ? null : salesDealId} onClose={() => openSalesDeal(null)} />;
  }

  return (
    <div className="h-full overflow-y-auto bg-surface-0">
      <div className="max-w-[1400px] mx-auto p-3 sm:p-5">
        <div className="panel overflow-hidden">
          {/* Тулбар: поиск + Фильтр + Excel + Добавить */}
          <div className="flex items-center justify-end gap-3 p-4 flex-wrap">
            <div className="relative flex-1 min-w-[160px] sm:flex-none">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-3" />
              <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                placeholder="Поиск" className="input h-10 pl-9 w-full sm:w-[260px]" />
            </div>
            <button className={cn('!h-10 px-4 rounded-lg font-semibold inline-flex items-center gap-2 transition-colors',
              filterCount(gf) > 0 ? 'bg-accent text-white' : 'bg-accent-soft text-accent hover:bg-accent-soft/70')} onClick={() => setFilterOpen(true)}>
              <Filter className="w-4 h-4" /> ФИЛЬТР{filterCount(gf) > 0 ? ` (${filterCount(gf)})` : ''}
            </button>
            <button className="!h-10 px-4 rounded-lg font-semibold inline-flex items-center gap-2 bg-accent-soft text-accent hover:bg-accent-soft/70 transition-colors" onClick={exportExcel}>
              <FileSpreadsheet className="w-4 h-4" /> EXCEL
            </button>
            {dperms.create && (
              <button className="btn-primary !h-10 font-semibold" onClick={() => openSalesDeal('new')}>
                <Plus className="w-4 h-4" /> ДОБАВИТЬ
              </button>
            )}
          </div>

          {/* Таблица */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-ink-3 border-y border-line bg-surface-2/40">
                  <th className="px-3 py-3 w-10"><input type="checkbox" className="w-4 h-4 accent-accent" checked={allChecked} onChange={toggleAll} /></th>
                  <th className="px-3 py-3 w-[90px] font-medium">#</th>
                  <th className="px-3 py-3 font-medium">Название</th>
                  <th className="px-3 py-3 w-[150px] font-medium">Время начала</th>
                  <th className="px-3 py-3 w-[150px] font-medium">Время конца</th>
                  <th className="px-3 py-3 w-[110px] font-medium">Стоимость</th>
                  <th className="px-3 py-3 w-[150px] font-medium">Дата создания</th>
                  <th className="px-3 py-3 w-[150px] font-medium">Дата изменения</th>
                  <th className="px-3 py-3 w-[90px] font-medium text-center">Действия</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={9} className="px-3 py-10 text-center text-ink-3">Ничего не найдено.</td></tr>
                )}
                {rows.map((d) => (
                  <tr key={d.id} className="border-b border-line/60 hover:bg-surface-2/40 transition-colors">
                    <td className="px-3 py-3"><input type="checkbox" className="w-4 h-4 accent-accent" checked={checked.has(d.id)} onChange={() => toggleOne(d.id)} /></td>
                    <td className="px-3 py-3">
                      <button className="text-accent hover:underline font-medium tabular-nums" onClick={() => openSalesDeal(d.id, navIds)}>#{d.number}</button>
                    </td>
                    <td className="px-3 py-3 text-ink-1">{dealEmoji(d, game_formats)} {gameTitle(d)}</td>
                    <td className="px-3 py-3 text-ink-2 tabular-nums whitespace-nowrap">{d.event.start_at != null ? fmtDateTime(d.event.start_at) : '—'}</td>
                    <td className="px-3 py-3 text-ink-2 tabular-nums whitespace-nowrap">{d.event.end_at != null ? fmtDateTime(d.event.end_at) : '—'}</td>
                    <td className="px-3 py-3 text-ink-1 tabular-nums">{d.amount != null ? d.amount.toLocaleString('ru-RU') : ''}</td>
                    <td className="px-3 py-3 text-ink-3 tabular-nums whitespace-nowrap">{fmtDateTime(d.created_at)}</td>
                    <td className="px-3 py-3 text-ink-3 tabular-nums whitespace-nowrap">{fmtDateTime(d.updated_at)}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-center gap-2">
                        {dperms.delete && (
                          <button className="icon-btn !w-8 !h-8 text-ink-3 hover:text-status-refused" title="Удалить" onClick={() => onDelete(d)}><Trash2 className="w-4 h-4" /></button>
                        )}
                        <button className="icon-btn !w-8 !h-8 text-ink-3 hover:text-accent" title="Редактировать" onClick={() => openSalesDeal(d.id, navIds)}><Pencil className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Пагинация */}
          <div className="flex items-center justify-end gap-4 p-4 text-xs text-ink-3">
            <span>Элементов на странице <span className="font-semibold text-ink-2">{PAGE_SIZE}</span></span>
            <span className="tabular-nums">{filtered.length === 0 ? '0' : `${curPage * PAGE_SIZE + 1}–${Math.min((curPage + 1) * PAGE_SIZE, filtered.length)}`} из {filtered.length}</span>
            <div className="flex items-center gap-1">
              <button className={cn('icon-btn !w-8 !h-8', curPage === 0 && 'opacity-40 cursor-default')} disabled={curPage === 0} onClick={() => setPage(curPage - 1)}>‹</button>
              <button className={cn('icon-btn !w-8 !h-8', curPage >= pageCount - 1 && 'opacity-40 cursor-default')} disabled={curPage >= pageCount - 1} onClick={() => setPage(curPage + 1)}>›</button>
            </div>
          </div>
        </div>
      </div>

      {filterOpen && (
        <GamesFilterModal
          initial={gf} formats={game_formats} sources={sources}
          onApply={(next) => { setGf(next); setPage(0); }}
          onClose={() => setFilterOpen(false)}
        />
      )}
    </div>
  );
}
